/**
 * Gemini Onboarding Contacts — executive contacts (CEO/COO/CFO/CNO…)
 * for the Gemini onboarding workflow (OR-1326).
 *
 * Unlike the rest of the UW form, this section persists IMMEDIATELY
 * (attach/detach against `/v2/submissions/{id}/onboarding-contacts/`)
 * instead of staging into the form's Save: contacts live in a global
 * pool, so a modal Save must write the pool row anyway for it to show
 * up in future searches (design note, OR-1310) — attaching in the same
 * gesture keeps the row and the server in lockstep with no diff pass.
 *
 * Row anatomy (design): Email is the interactive cell — a SearchSelect
 * over the pool with a permanent "Add New" action that opens the
 * creation modal. Name / Job Title / Phone render read-only from the
 * attached contact (editing them inline would PATCH the shared pool
 * row and leak into other submissions; fork-via-modal instead).
 * Swapping a row's email detaches the old contact and attaches the
 * new one.
 *
 * Create mode: attaching needs a real submission id, so the section
 * gates its CTAs until the submission is saved.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trash02, UserCircle } from "@untitledui/icons";
import { Button, ButtonUtility, SearchSelect, Table } from "@tricura/atlas-ui";
import {
    attachOnboardingContact,
    detachOnboardingContact,
    listOnboardingContacts,
    searchContactPool,
} from "../../../../api";
import { FormGroup } from "../../../../components/form-group";
import { OnboardingContactModal } from "../../../../components/onboarding-contact-modal";
import { TableEmptyState } from "../../../../components/table-empty-state";
import { useDebouncedValue } from "../../../../hooks/use-debounced-value";
import type { OnboardingContact } from "../../../../types/onboarding-contact";
import { displayJobTitle } from "../../../../types/onboarding-contact";
import { useSubmissionContext } from "../../context";

/** A table row: an attached contact, or a pending "Select Email" row
 *  the user just added and hasn't resolved yet (local-only). */
interface Row {
    key: string;
    contact: OnboardingContact | null;
}

let pendingSeq = 0;
const pendingKey = () => `pending-${++pendingSeq}`;

const READONLY_CELL = "text-quaternary text-sm";

export function GeminiOnboardingContactsSection() {
    const { detail, mode } = useSubmissionContext();
    const submissionId = mode === "edit" ? detail.id : "";

    const [rows, setRows] = useState<Row[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    /** Which row the "Add New" modal is resolving for (its key). */
    const [modalRowKey, setModalRowKey] = useState<string | null>(null);

    // Load the attached list once per submission.
    useEffect(() => {
        if (!submissionId) {
            setLoaded(true);
            return;
        }
        let cancelled = false;
        listOnboardingContacts(submissionId)
            .then((contacts) => {
                if (cancelled) return;
                setRows(contacts.map((c) => ({ key: c.id, contact: c })));
                setLoaded(true);
            })
            .catch((error) => {
                console.error("Failed to load onboarding contacts", error);
                if (cancelled) return;
                // Without this the table renders empty, which is
                // indistinguishable from "no contacts yet".
                setActionError("Couldn't load the onboarding contacts. Reload to try again.");
                setLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [submissionId]);

    const attachedIds = useMemo(() => new Set(rows.map((r) => r.contact?.id).filter(Boolean)), [rows]);

    const addPendingRow = useCallback(() => {
        setRows((prev) => [{ key: pendingKey(), contact: null }, ...prev]);
    }, []);

    /** Attach `contact` to the submission and settle it into `rowKey`.
     *  Swapping detaches the row's previous contact first.
     *
     *  Resolves to `null` on success, or to the reason it failed. The row
     *  picker leaves that reason in `actionError` under the table, while the
     *  modal rethrows it so the message lands inside the dialog the user is
     *  looking at — the section-level line would be hidden behind it. */
    const resolveRow = useCallback(
        async (rowKey: string, contact: OnboardingContact): Promise<string | null> => {
            setActionError(null);
            const row = rows.find((r) => r.key === rowKey);
            if (!row || !submissionId) return "That row is no longer available.";
            if (attachedIds.has(contact.id) && row.contact?.id !== contact.id) {
                const reason = "That contact is already on this submission.";
                setActionError(reason);
                return reason;
            }
            try {
                if (row.contact) await detachOnboardingContact(submissionId, row.contact.id);
                const attached = await attachOnboardingContact(submissionId, contact.id);
                setRows((prev) => prev.map((r) => (r.key === rowKey ? { key: attached.id, contact: attached } : r)));
                return null;
            } catch (error) {
                console.error("Failed to attach onboarding contact", error);
                const reason = error instanceof Error ? error.message : "Failed to attach contact";
                setActionError(reason);
                return reason;
            }
        },
        [rows, submissionId, attachedIds],
    );

    const removeRow = useCallback(
        async (rowKey: string) => {
            setActionError(null);
            const row = rows.find((r) => r.key === rowKey);
            if (!row) return;
            if (!row.contact) {
                setRows((prev) => prev.filter((r) => r.key !== rowKey));
                return;
            }
            try {
                await detachOnboardingContact(submissionId, row.contact.id);
                setRows((prev) => prev.filter((r) => r.key !== rowKey));
            } catch (error) {
                console.error("Failed to detach onboarding contact", error);
                setActionError(error instanceof Error ? error.message : "Failed to remove contact");
            }
        },
        [rows, submissionId],
    );

    const canAdd = Boolean(submissionId);

    return (
        <FormGroup
            title="Gemini Onboarding Contacts"
            flushBody
            action={
                <Button color="link-gray" size="sm" onClick={addPendingRow} isDisabled={!canAdd} data-autotest="uw-information-gemini-onboarding-contacts-add-row-button">
                    + Add Row
                </Button>
            }
        >
            <div className="relative overflow-hidden">
                <Table
                    aria-label="Gemini Onboarding Contacts"
                    wrapperClassName="min-h-80"
                    className="[&_tbody_tr:last-child]:border-b-0!"
                >
                    <Table.Header>
                        <Table.Head id="email" label="Email" isRowHeader className="min-w-72 pl-9" />
                        <Table.Head id="name" label="Name" className="min-w-48" />
                        <Table.Head id="jobTitle" label="Job Title" className="w-40" />
                        <Table.Head id="phone" label="Phone" className="w-44" />
                        <Table.Head id="actions" label="" className="w-16" />
                    </Table.Header>
                    <Table.Body items={rows}>
                        {(row) => (
                            <Table.Row id={row.key} data-autotest="uw-information-gemini-onboarding-contacts-row">
                                <Table.Cell className="min-w-72">
                                    <RowEmailSelect
                                        row={row}
                                        attachedIds={attachedIds}
                                        onPick={(contact) => resolveRow(row.key, contact)}
                                        onAddNew={() => setModalRowKey(row.key)}
                                    />
                                </Table.Cell>
                                <Table.Cell className="min-w-48">
                                    <span className={READONLY_CELL} data-autotest="uw-information-gemini-onboarding-contacts-name-value">
                                        {row.contact?.name || "Name"}
                                    </span>
                                </Table.Cell>
                                <Table.Cell className="w-40">
                                    <span className={READONLY_CELL} data-autotest="uw-information-gemini-onboarding-contacts-job-title-value">
                                        {row.contact ? displayJobTitle(row.contact.jobTitle) : "Job Title"}
                                    </span>
                                </Table.Cell>
                                <Table.Cell className="w-44">
                                    <span className={READONLY_CELL} data-autotest="uw-information-gemini-onboarding-contacts-phone-value">
                                        {row.contact?.phone || "(555) 000-0000"}
                                    </span>
                                </Table.Cell>
                                <Table.Cell>
                                    <div className="flex justify-end">
                                        <ButtonUtility
                                            color="tertiary"
                                            size="sm"
                                            icon={Trash02}
                                            tooltip="Delete row"
                                            onClick={() => removeRow(row.key)}
                                            data-autotest="uw-information-gemini-onboarding-contacts-delete-row-button"
                                            className="hover:bg-[var(--color-utility-neutral-100)]"
                                        />
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>

                {loaded && rows.length === 0 && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="pointer-events-auto">
                            <TableEmptyState
                                icon={UserCircle}
                                title="Gemini Onboarding Contacts"
                                description={
                                    canAdd
                                        ? "Use the button below to start building the Gemini Onboarding Contacts list"
                                        : "Save the submission first to start adding contacts"
                                }
                                onAdd={canAdd ? addPendingRow : undefined}
                                data-autotest="uw-information-gemini-onboarding-contacts-empty-add-row-button"
                            />
                        </div>
                    </div>
                )}
            </div>

            {actionError && <p className="text-error-primary px-6 pb-4 text-sm">{actionError}</p>}

            <OnboardingContactModal
                isOpen={modalRowKey !== null}
                onOpenChange={(open) => {
                    if (!open) setModalRowKey(null);
                }}
                onDone={async (contact) => {
                    // Throwing keeps the modal open AND shows the reason inside
                    // it (the modal renders whatever onDone throws). It used to
                    // close unconditionally, so a contact that was never
                    // attached looked like a success.
                    if (modalRowKey) {
                        const reason = await resolveRow(modalRowKey, contact);
                        if (reason) throw new Error(reason);
                    }
                    setModalRowKey(null);
                }}
            />
        </FormGroup>
    );
}

// ─── Row email selector ────────────────────────────────────────────────
//
// SearchSelect over the pool with server-side search-as-you-type (same
// debounce pattern as the Brokerage picker) and a permanent "Add New"
// action. Already-attached contacts are filtered out so a duplicate
// attach can't be picked; the row's own contact stays pinned so the
// trigger keeps its label between searches.

interface RowEmailSelectProps {
    row: Row;
    attachedIds: Set<string | undefined>;
    onPick: (contact: OnboardingContact) => void;
    onAddNew: () => void;
}

function RowEmailSelect({ row, attachedIds, onPick, onAddNew }: RowEmailSelectProps) {
    const [results, setResults] = useState<OnboardingContact[]>([]);
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebouncedValue(query.trim(), 300);

    useEffect(() => {
        let cancelled = false;
        searchContactPool(debouncedQuery, 20)
            .then((contacts) => {
                if (!cancelled) setResults(contacts);
            })
            .catch((error) => console.error("Failed to search contacts", error));
        return () => {
            cancelled = true;
        };
    }, [debouncedQuery]);

    const options = useMemo(() => {
        const selectable = results.filter((c) => c.id === row.contact?.id || !attachedIds.has(c.id));
        return row.contact && !selectable.some((c) => c.id === row.contact?.id) ? [row.contact, ...selectable] : selectable;
    }, [results, row.contact, attachedIds]);

    const items = options.map((c) => ({ id: c.id, label: c.email, supportingText: c.name || "—" }));

    return (
        <SearchSelect
            size="sm"
            aria-label="Email"
            placeholder="Select Email"
            items={items}
            selectedKey={row.contact?.id ?? null}
            onSearchChange={setQuery}
            onSelectionChange={(k) => {
                const contact = options.find((c) => c.id === k);
                if (contact && contact.id !== row.contact?.id) onPick(contact);
            }}
            actionLabel="Add New"
            onAction={onAddNew}
            actionDataAutotest="uw-information-gemini-onboarding-contacts-email-add-new-button"
            data-autotest="uw-information-gemini-onboarding-contacts-email-select"
            searchDataAutotest="uw-information-gemini-onboarding-contacts-email-search-input"
        >
            {(item) => (
                <SearchSelect.Item
                    id={item.id}
                    supportingText={item.supportingText}
                    data-autotest="uw-information-gemini-onboarding-contacts-email-option"
                >
                    {item.label}
                </SearchSelect.Item>
            )}
        </SearchSelect>
    );
}
